import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Clock, LogIn, LogOut, Coffee, Play, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DataTable from "@/components/DataTable";
import TeamLeadFilter from "@/components/TeamLeadFilter";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate, fmtTime, fmtDateTime, fmtMinutes, fmtHours } from "@/lib/attendance";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

const STATUS_OPTIONS = [
  { value: "_all_", label: "All statuses" },
  { value: "present", label: "Present" },
  { value: "half_day_lop", label: "Half Day (LOP)" },
  { value: "absent", label: "Absent" },
  { value: "leave", label: "Leave" },
  { value: "holiday", label: "Holiday" },
  { value: "weekend", label: "Weekend" },
];

export default function Attendance() {
  const { user } = useAuth();
  const [refresh, setRefresh] = useState(0);
  const [records, setRecords] = useState([]);
  const [today, setToday] = useState(null);
  const [month, setMonth] = useState(startOfMonth(new Date()));

  useEffect(() => {
    api.get("/attendance").then((r) => {
      setRecords(r.data);
      const t = new Date().toISOString().slice(0, 10);
      const mine = r.data.filter((a) => a.employee_id === user?.employee_id);
      setToday(mine.find((a) => a.date === t) || null);
    });
  }, [refresh, user]);

  const checkIn = async () => {
    try { await api.post("/attendance/check-in"); toast.success("Checked in"); setRefresh((v)=>v+1); }
    catch { toast.error("Failed to check in"); }
  };
  const checkOut = async () => {
    try { await api.post("/attendance/check-out"); toast.success("Checked out"); setRefresh((v)=>v+1); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const breakStart = async () => {
    try { await api.post("/attendance/break/start"); toast.success("Break started"); setRefresh((v)=>v+1); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to start break"); }
  };
  const breakEnd = async () => {
    try { await api.post("/attendance/break/end"); toast.success("Break ended"); setRefresh((v)=>v+1); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to end break"); }
  };

  // Open break (if any) — for the "on break since…" badge in the today card
  const openBreak = useMemo(
    () => (today?.breaks || []).find((b) => !b.end_at) || null,
    [today]
  );
  const onBreak = !!openBreak;
  const isCheckedOut = !!today?.check_out;

  // Build calendar cells for current user
  const calendar = useMemo(() => {
    const first = startOfMonth(month);
    const total = daysInMonth(month);
    const startDay = first.getDay(); // 0 Sun .. 6 Sat
    const mine = records.filter((r) => r.employee_id === user?.employee_id);
    const map = Object.fromEntries(mine.map((r) => [r.date, r]));
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= total; d++) {
      const date = new Date(month.getFullYear(), month.getMonth(), d);
      const iso = date.toISOString().slice(0, 10);
      cells.push({ date, iso, rec: map[iso] });
    }
    return cells;
  }, [records, user, month]);

  const monthLabel = month.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div data-testid="attendance-page">
      <PageHeader
        title="Attendance"
        subtitle="Calendar view of your daily check-ins, plus team attendance table."
      />

      <Card className="hr-card p-5 mb-6 flex items-center justify-between flex-wrap gap-4" data-testid="attendance-today-card">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg grid place-items-center ${
            onBreak ? "bg-amber-100 text-amber-700" : "bg-[#1E88E5]/10 text-[#1E88E5]"
          }`}>
            {onBreak ? <Coffee className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">
              Today · {fmtDate(new Date().toISOString().slice(0, 10))}
            </div>
            <div className="text-sm font-medium" data-testid="today-status-line">
              {(() => {
                if (!today) return "Not checked in yet.";
                if (isCheckedOut) {
                  return (
                    <>
                      In <span className="font-mono">{fmtTime(today.check_in_at) || today.check_in}</span>
                      {" · "}Out <span className="font-mono">{fmtTime(today.check_out_at) || today.check_out}</span>
                      {today.break_minutes ? ` · Break ${fmtMinutes(today.break_minutes)}` : ""}
                      {today.net_hours ? ` · Net ${today.net_hours} h` : ""}
                    </>
                  );
                }
                if (onBreak) {
                  return (
                    <>
                      <span className="text-amber-700 font-semibold">On break</span> since{" "}
                      <span className="font-mono">{fmtTime(openBreak.start_at) || openBreak.start}</span>
                      {today.break_minutes ? ` · Total break so far ${fmtMinutes(today.break_minutes)}` : ""}
                    </>
                  );
                }
                return <>Checked in at <span className="font-mono">{fmtTime(today.check_in_at) || today.check_in}</span></>;
              })()}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!today && (
            <Button data-testid="check-in-btn" onClick={checkIn} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
              <LogIn className="h-4 w-4 mr-1.5" /> Check in
            </Button>
          )}
          {today && !isCheckedOut && !onBreak && (
            <>
              <Button data-testid="break-start-btn" onClick={breakStart} variant="outline" className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50">
                <Coffee className="h-4 w-4 mr-1.5" /> Start break
              </Button>
              <Button data-testid="check-out-btn" onClick={checkOut} variant="outline" className="h-9">
                <LogOut className="h-4 w-4 mr-1.5" /> Check out
              </Button>
            </>
          )}
          {today && !isCheckedOut && onBreak && (
            <Button data-testid="break-end-btn" onClick={breakEnd} className="h-9 bg-amber-600 hover:bg-amber-700">
              <Play className="h-4 w-4 mr-1.5" /> End break
            </Button>
          )}
          {isCheckedOut && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md">
              Day completed — see you tomorrow!
            </div>
          )}
        </div>
      </Card>

      <Tabs defaultValue="calendar">
        <TabsList className="bg-transparent border-b border-gray-200 rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
          <TabsTrigger value="calendar" data-testid="tab-calendar" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">My calendar</TabsTrigger>
          {user?.role !== "employee" && (
            <TabsTrigger value="team" data-testid="tab-team" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Team log</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="calendar">
          <Card className="hr-card p-5" data-testid="attendance-calendar">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg font-semibold text-[#0F172A]">{monthLabel}</div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-2 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="text-[10px] uppercase tracking-wider text-gray-400 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {calendar.map((cell, i) => {
                if (!cell) return <div key={`blank-${i}`} />;
                const isToday = cell.iso === new Date().toISOString().slice(0, 10);
                const status = cell.rec?.status;
                const bgByStatus = (() => {
                  if (status === "present") return "bg-emerald-50 border-emerald-200";
                  if (status === "half_day_lop") return "bg-amber-50 border-amber-200";
                  if (status === "absent") return "bg-red-50 border-red-200";
                  return "bg-white border-gray-200";
                })();
                const bg = bgByStatus;
                const ci = cell.rec?.check_in_at ? fmtTime(cell.rec.check_in_at) : cell.rec?.check_in;
                const co = cell.rec?.check_out_at ? fmtTime(cell.rec.check_out_at) : cell.rec?.check_out;
                return (
                  <div
                    key={cell.iso}
                    data-testid={`cal-day-${cell.iso}`}
                    title={cell.rec ? `Status: ${status}${cell.rec.net_hours ? ` · Net ${cell.rec.net_hours} h` : ""}${cell.rec.break_minutes ? ` · Break ${fmtMinutes(cell.rec.break_minutes)}` : ""}` : ""}
                    className={`border ${bg} rounded-md p-2 min-h-[64px] text-xs ${isToday ? "ring-2 ring-[#F26B3A]" : ""}`}
                  >
                    <div className="font-medium">{cell.date.getDate()}</div>
                    {cell.rec && (
                      <>
                        <div className="text-[10px] text-gray-600 mt-0.5 font-mono leading-tight">
                          {ci || "—"}
                          {co && <><br />→ {co}</>}
                        </div>
                        {cell.rec.net_hours ? (
                          <div className="text-[9px] text-gray-500 mt-0.5">Net {cell.rec.net_hours}h</div>
                        ) : null}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="team">
          <AttendanceTeamReport refresh={refresh} records={records} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AttendanceTeamReport({ refresh, records }) {
  const [employees, setEmployees] = useState([]);
  const [tlFilter, setTlFilter] = useState("_all_");
  const [statusFilter, setStatusFilter] = useState("_all_");
  const [deptFilter, setDeptFilter] = useState("_all_");
  const [empSearch, setEmpSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, [refresh]);

  const empById = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.employee_id] = e; });
    return m;
  }, [employees]);

  const leaderName = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.employee_id] = e.name; });
    return m;
  }, [employees]);

  const departments = useMemo(() => {
    const s = new Set(employees.map((e) => e.department).filter(Boolean));
    return Array.from(s).sort();
  }, [employees]);

  const combinedFilter = useMemo(
    () => (r) => {
      const e = empById[r.employee_id];
      if (tlFilter && tlFilter !== "_all_" && e?.team_lead_id !== tlFilter) return false;
      if (deptFilter && deptFilter !== "_all_" && e?.department !== deptFilter) return false;
      if (statusFilter && statusFilter !== "_all_" && r.status !== statusFilter) return false;
      if (fromDate && (r.date || "") < fromDate) return false;
      if (toDate && (r.date || "") > toDate) return false;
      if (empSearch) {
        const q = empSearch.toLowerCase();
        const hit = (r.employee_id || "").toLowerCase().includes(q)
          || (e?.name || "").toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    },
    [tlFilter, deptFilter, statusFilter, fromDate, toDate, empSearch, empById]
  );

  const downloadExport = (fmt) => {
    const token = localStorage.getItem("hr_token");
    const params = new URLSearchParams({ fmt, auth: token || "" });
    if (tlFilter && tlFilter !== "_all_") params.set("team_lead_id", tlFilter);
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/attendance/export?${params.toString()}`, "_blank");
  };

  return (
    <DataTable
      testId="attendance-table"
      data={records}
      refreshKey={refresh}
      searchPlaceholder="Search by Employee ID, Name, TL, Department, Status…"
      toolbar={(
        <div className="flex items-center gap-2 flex-wrap" data-testid="attendance-filters">
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-testid="att-from-date"
            className="h-9 w-[150px]"
            title="From date"
          />
          <span className="text-xs text-gray-400">→</span>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="att-to-date"
            className="h-9 w-[150px]"
            title="To date"
          />
          <Input
            placeholder="Employee ID / name…"
            value={empSearch}
            onChange={(e) => setEmpSearch(e.target.value)}
            data-testid="att-emp-search"
            className="h-9 w-[180px]"
          />
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="att-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="att-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <TeamLeadFilter value={tlFilter} onChange={setTlFilter} testIdPrefix="attendance-tl-filter" />
          <Button size="sm" variant="outline" onClick={() => downloadExport("csv")} data-testid="att-export-csv" className="h-9"><Download className="h-3.5 w-3.5 mr-1.5" />CSV</Button>
          <Button size="sm" variant="outline" onClick={() => downloadExport("xlsx")} data-testid="att-export-xlsx" className="h-9"><Download className="h-3.5 w-3.5 mr-1.5" />Excel</Button>
          <Button size="sm" variant="outline" onClick={() => downloadExport("pdf")} data-testid="att-export-pdf" className="h-9"><Download className="h-3.5 w-3.5 mr-1.5" />PDF</Button>
        </div>
      )}
      filterFn={combinedFilter}
      columns={[
        { key: "_sl", label: "Sl. No", render: (_, i) => i + 1 },
        { key: "employee_id", label: "Employee ID", mono: true, render: (r) => (
          <span className="font-mono text-xs" data-testid={`att-emp-id-${r.employee_id}`}>{r.employee_id}</span>
        ) },
        { key: "_name", label: "Employee Name", render: (r) => empById[r.employee_id]?.name || "—" },
        { key: "_dept", label: "Department", render: (r) => empById[r.employee_id]?.department || "—" },
        { key: "_tl", label: "Team Lead", render: (r) => leaderName[empById[r.employee_id]?.team_lead_id] || "—" },
        { key: "_mgr", label: "Reporting Manager", render: (r) => leaderName[empById[r.employee_id]?.reporting_manager_id] || "—" },
        { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
        { key: "check_in_at", label: "Check-In", mono: true, render: (r) => r.check_in_at ? fmtDateTime(r.check_in_at) : (r.check_in || "—") },
        { key: "check_out_at", label: "Check-Out", mono: true, render: (r) => r.check_out_at ? fmtDateTime(r.check_out_at) : (r.check_out || "—") },
        { key: "hours", label: "Logged Hrs", align: "right", render: (r) => fmtHours(r.hours) },
        { key: "break_minutes", label: "Break", align: "right", render: (r) => fmtMinutes(r.break_minutes) },
        { key: "net_hours", label: "Net Hrs", align: "right", render: (r) => fmtHours(r.net_hours || (r.hours ? Math.max(0, r.hours - (r.break_minutes || 0) / 60) : 0)) },
        { key: "late_minutes", label: "Late In", align: "right", render: (r) => fmtMinutes(r.late_minutes) },
        { key: "early_minutes", label: "Early Out", align: "right", render: (r) => fmtMinutes(r.early_minutes) },
        { key: "ot_hours", label: "OT", align: "right", render: (r) => fmtHours(r.ot_hours) },
        { key: "status", label: "Status", badge: true },
      ]}
    />
  );
}
